import { FHIR_DEFAULT_PAGE_URL, FhirService } from './../service/fhir.service';
import { UserService } from './../service/user.service';
import { SettingService } from './../service/setting.service';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { AuthenticationService } from '../authentication/authentication.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
@Injectable({
  providedIn: 'root'
})
export class AuthGuardService 
{
  constructor(
    private authService: AuthenticationService,
    private userService: UserService,
    private fhirService: FhirService,
    private router: Router,
    private settingService: SettingService)
  {
  }

  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean
  {
    // FHIR Context - Start: Checking if token is passed in the url (HOME was clicked for Smart-On-FHIR context)
    if (route.queryParams.fhirPatient)
    {
      this.fhirService.savePatientContextToStorage(JSON.parse(decodeURIComponent(route.queryParams.fhirPatient)));
      this.fhirService.savePatientContextFullSiteToStorage(true); // New open tab must be fullSite to work properly
    }
    if (route.queryParams.token) { this.authService.saveUserToStorage(JSON.parse(decodeURIComponent(route.queryParams.token))); }
    // FHIR Context - End

    if (this.authService.isAuthenticated)
    {
      return this.settingService.getCachedSessionSettings()
        .pipe(
          map(sessionSetting =>
          {
            let accessOK = true;

            if (this.fhirService.fullSite)
            {
              accessOK = true;
            }
            else
              // If FHIR embedded, allow only the summary page
              if (this.fhirService.embedded && !state.url.includes(FHIR_DEFAULT_PAGE_URL))
              {
                accessOK = false;
              }

            // Check left-menu access role for specific urls
            if (accessOK)
            {
              switch (state.url)
              {
                case '/reports':
                  accessOK = this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.reportRoles);
                  break;
                case '/community-services':
                  accessOK = sessionSetting.communityEnabled &&
                    this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.addUpdateCommunityResourcesRoles);
                  break;
                case '/community-services-search':
                  accessOK = sessionSetting.communityEnabled &&
                    this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.communityServiceSearchRoles);
                  break;
                case '/providers':
                  if (this.authService.currentUser.role !== 'ROLE_PROGRAM_LEAD') 
                  {
                    accessOK = sessionSetting.providerEnabled &&
                      this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.providersRoles);
                  }
                  else
                  {
                    accessOK = (sessionSetting.addUpdateProvidersRoles !== '') ?
                      this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.addUpdateProvidersRoles) : false;
                  }
                  break;
                case '/providers-search':
                  accessOK = sessionSetting.providerEnabled &&
                    this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.providersRoles);
                  break;
                case '/kno2-search':
                  accessOK = sessionSetting.kno2Enabled &&
                    this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.providersRoles);
                  break;
                case '/messages/inbox':
                case '/messages/sent':
                case '/messages/deleted':
                  accessOK = this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.messagesRoles);
                  break;
                case '/analytics':
                  accessOK = this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.analyticsRoles);
                  break;
                case '/groups':
                  if (this.authService.currentUser.role === 'ROLE_COMMUNITY_COORDINATOR' || this.authService.currentUser.role === 'ROLE_PROGRAM_LEAD' ||
                    this.authService.currentUser.role === 'ROLE_OPERATOR') 
                  {
                    accessOK = (sessionSetting.allowOrganizationGroupsRoles !== '') ?
                      this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.allowOrganizationGroupsRoles) : false;
                  }
                  break;
                case '/users':
                  if (this.authService.currentUser.role === 'ROLE_PROGRAM_LEAD' || this.authService.currentUser.role === 'ROLE_OPERATOR') 
                  {
                    accessOK = (sessionSetting.allowOrganizationUsersRoles !== '') ?
                      this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.allowOrganizationUsersRoles) : false;
                  }
                  break;
                case '/team-roles':
                  if (this.authService.currentUser.role === 'ROLE_PROGRAM_LEAD') 
                  {
                    accessOK = (sessionSetting.addUpdateTeamRolesRoles !== '') ?
                      this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.addUpdateTeamRolesRoles) : false;
                  }
                  break;
                case '/assessment-types':
                  if (this.authService.currentUser.role === 'ROLE_PROGRAM_LEAD') 
                  {
                    accessOK = (sessionSetting.addUpdateAssessmentTypesRoles !== '') ?
                      this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.addUpdateAssessmentTypesRoles) : false;
                  }
                  break;
                case '/assessments':
                  if (this.authService.currentUser.role === 'ROLE_PROGRAM_LEAD') 
                  {
                    accessOK = (sessionSetting.addUpdateAssessmentsRoles !== '') ?
                      this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.addUpdateAssessmentsRoles) : false;
                  }
                  break;
                case '/program-templates':
                  if (this.authService.currentUser.role === 'ROLE_PROGRAM_LEAD') 
                  {
                    accessOK = (sessionSetting.addUpdateProgramTemplatesRoles !== '') ?
                      this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.addUpdateProgramTemplatesRoles) : false;
                  }
                  break;
                case '/facilities':
                  if (this.authService.currentUser.role === 'ROLE_PROGRAM_LEAD') 
                  {
                    accessOK = (sessionSetting.addUpdateFacilitiesRoles !== '') ?
                      this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.addUpdateFacilitiesRoles) : false;
                  }
                  break;
                case '/patients-import':
                  if (this.authService.currentUser.role === 'ROLE_PROGRAM_LEAD') 
                  {
                    accessOK = (sessionSetting.addUpdateClientImportRoles !== '') ?
                      this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.addUpdateClientImportRoles) : false;
                  }
                  break;

                case '/channel-categories':
                  if (this.authService.currentUser.role === 'ROLE_PROGRAM_LEAD') 
                  {
                    accessOK = (sessionSetting.addUpdateChannelCategoriesRoles !== '') ?
                      this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.addUpdateChannelCategoriesRoles) : false;
                  }
                  break;
                case '/dictionaries':
                  if (this.authService.currentUser.role === 'ROLE_PROGRAM_LEAD') 
                  {
                    accessOK = (sessionSetting.addUpdateDictionariesRoles !== '') ?
                      this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.addUpdateDictionariesRoles) : false;
                  }
                  break;
                case '/extensions':
                  if (this.authService.currentUser.role === 'ROLE_PROGRAM_LEAD') 
                  {
                    accessOK = (sessionSetting.addUpdateExtensionsRoles !== '') ?
                      this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.addUpdateExtensionsRoles) : false;
                  }
                  break;
                case '/field-configurations':
                  if (this.authService.currentUser.role === 'ROLE_PROGRAM_LEAD') 
                  {
                    accessOK = (sessionSetting.addUpdateFieldConfigurationsRoles !== '') ?
                      this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.addUpdateFieldConfigurationsRoles) : false;
                  }
                  break;
                case '/metrics':
                  if (this.authService.currentUser.role === 'ROLE_PROGRAM_LEAD') 
                  {
                    accessOK = (sessionSetting.addUpdateMetricsRoles !== '') ?
                      this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.addUpdateMetricsRoles) : false;
                  }
                  break;
                case '/program-categories':
                  if (this.authService.currentUser.role === 'ROLE_PROGRAM_LEAD') 
                  {
                    accessOK = (sessionSetting.addUpdateProgramCategoriesRoles !== '') ?
                      this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.addUpdateProgramCategoriesRoles) : false;
                  }
                  break;
                case '/community-service-categories':
                  if (this.authService.currentUser.role === 'ROLE_PROGRAM_LEAD') 
                  {
                    accessOK = (sessionSetting.addUpdateServiceCategoriesRoles !== '') ?
                      this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.addUpdateServiceCategoriesRoles) : false;
                  }
                  break;
                case '/terms-and-conditions':
                  if (this.authService.currentUser.role === 'ROLE_PROGRAM_LEAD') 
                  {
                    accessOK = (sessionSetting.addUpdateTermsAndConditionsRoles !== '') ?
                      this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.addUpdateTermsAndConditionsRoles) : false;
                  }
                  break;
              }
            }

            // Check if password changes is required, then prevent all routes
            if (this.authService?.currentUser?.changePassword) 
            {
              this.redirectLogin(state)
            }
            else if (!accessOK)
            {
              this.router.navigate(['/404']);
            }
            return accessOK;
          }));
    }
    else
    {
      this.redirectLogin(state)
      return false;
    }
  }

  private redirectLogin(state: RouterStateSnapshot): void
  {
    if (state.url !== '/')
    {
      this.router.navigate(['/login'], { queryParams: { return: state.url } });
    }
    else
    {
      this.router.navigate(['/login']);
    }
  }
}
